import {
	PropsWithChildren,
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	ConfirmationDialogInitialData,
	DetailsBox,
	DetailsBoxAction,
	DetailsBoxActionGroup,
	DetailsBoxVariant,
	DropOver,
	FontIconName,
	FormattedText,
	Toolbar,
	useConfirmationDialog
} from "client/Components";
import {
	AlertManager,
	useAlerts,
	useStableImperativeCallback,
	useUnmountedCancellation
} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import styles from "./ViewAppointmentBox.module.less";
import {
	EMPTY_ARRAY,
	generateTrackingId,
	isNil,
	isNotNil,
	isNotNilOrWhiteSpace
} from "client/Misc/utils";
import { OnlineMeetingButton } from "../../OnlineMeetingButton";
import { groupBy } from "client_legacy/ndaw/arrayHelpers";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { AppointmentSpan } from "../../AppointmentSpan";
import { Coordinate } from "client/Misc/commonTypes";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	ReminderText,
	ResponseKind,
	sendAppointmentResponse
} from "../../AppointmentDetails/AppointmentDetailsDialog";
import {
	AppointmentDetailModel,
	AppointmentDetailViewModel,
	AppointmentResponseKind,
	AppointmentResponseStatus,
	AttendeeKind,
	AttendeeModel,
	AttendingStatus,
	deleteAppointment,
	getAppointmentDetails,
	RecurringAppointmentOperationKind
} from "client/Api/Calendar/calendar";
import { RespondId } from "../../events";
import { formatCalendarLocations } from "../../calendarUtils";
import { CancelAppointmentDialog } from "../CancelAppointmentDialog";
import { EditAppointmentInfo } from "../../CalendarPage";
import { never } from "client_legacy/ndaw/errors";
import { ExchangeMessageBodyKind } from "client/Api/Email/common";
import { OrganizerDetail } from "./OrganizerDetail";
import {
	AppointmentInformation,
	AppointmentOrIdentifier,
	DetailsAppointmentInformation,
	isAppointmentInformation
} from "../../common";
import { htmlToPlainText } from "client/Misc/htmlUtils";

type ViewAppointmentBoxProps = {
	color: DetailsBoxVariant;
	appointmentOrIdentifier: AppointmentOrIdentifier;
	target: HTMLElement;
	coordinate: Coordinate;
	trackingId: string;
	onClose: () => void;
	onEnlarge: () => void;
	timezone: TimeZone;
	onEdit: (
		coordinate: Coordinate,
		data: EditAppointmentInfo
	) => void;
	onAfterCancelOrDelete: (calendarIds: readonly string[]) => void;
};

export const ViewAppointmentBox = (props: PropsWithChildren<ViewAppointmentBoxProps>): ReactElement => {
	const {
		color,
		appointmentOrIdentifier,
		target,
		coordinate,
		trackingId,
		onClose: onCloseFromProps,
		onEnlarge,
		onEdit,
		timezone,
		onAfterCancelOrDelete
	} = props;

	const [appointment, setAppointment] = useState<AppointmentInformation | null>(null);
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [cancelDialogState, setCancelDialogState] = useState<CancelDialogState>({ isOpen: false });
	const [actions, setActions] = useState<readonly DetailsBoxAction[]>();

	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const alertManager: AlertManager = useAlerts();

	const onClose: () => void = useStableImperativeCallback(onCloseFromProps);

	useEffect(
		(): EffectWithDestructorResult => {
			if (isAppointmentInformation(appointmentOrIdentifier)) {
				setAppointment(appointmentOrIdentifier);
				return;
			}

			const load = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const viewModel: AppointmentDetailViewModel = await getAppointmentDetails(appointmentOrIdentifier, signal);
					setAppointment({
						...viewModel.model,
						kind: "details"
					});
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);

			return (): void => cancellation.abort();
		},
		[
			alertManager,
			appointmentOrIdentifier,
			onClose
		]
	);

	const closeCancelAppointmentDialogHandler = useCallback(
		(wasCancelled?: boolean) => {
			setCancelDialogState({ isOpen: false });

			onClose();
			if (wasCancelled === true && appointment?.kind === "details") {
				onAfterCancelOrDelete([
					appointment.calendar
						.id
				]);
			}
		},
		[
			onAfterCancelOrDelete,
			appointment,
			onClose
		]
	);

	const renderContent = (): ReactElement | null => {
		if (!appointment) {
			return null;
		}

		switch(appointment.kind) {
			case "basic":
				return (
					<DetailsBox.List>
						<DetailsBox.Item icon="clock">
							<AppointmentSpan
								appointment={appointment}
								timezone={timezone}
							/>
						</DetailsBox.Item>
					</DetailsBox.List>
				);
			case "details":
				return (
					<Details
						appointment={appointment}
						coordinate={coordinate}
						onActionsChange={setActions}
						onAfterCancelOrDelete={onAfterCancelOrDelete}
						onCancelDialogStateChange={setCancelDialogState}
						onClose={onClose}
						onEdit={onEdit}
						onIsBusyChange={setIsBusy}
						openConfirmationDialog={openConfirmationDialog}
						timezone={timezone}
					/>
				);
			default:
				never(appointment);
		}
	};

	return (
		<>
			<DropOver
				target={target}
				targetAlignment={{
					kind: "absolute",
					coordinate
				}}
				onClose={confirmationDialog || cancelDialogState.isOpen
					? undefined
					: onClose
				}
			>
				<DetailsBox
					trackingId={generateTrackingId(trackingId, "__dropover")}
					variant={color}
					title={
						isNotNil(appointment)
							? appointment.subject || localize("_NoSubject")
							: ""
					}
					onClose={onClose}
					onEnlarge={onEnlarge}
					hasBusyIndicator={isBusy}
					actions={actions}
				>
					{renderContent()}
				</DetailsBox>
			</DropOver>
			{confirmationDialog}
			{appointment?.kind === "details" && cancelDialogState.isOpen && (
				<CancelAppointmentDialog
					appointmentIdentifier={{
						id: appointment.id,
						calendarId: appointment.calendar.id
					}}
					onClose={closeCancelAppointmentDialogHandler}
					recurringOperationKind={cancelDialogState.recurringAppointmentOperationKind}
				/>
			)}
		</>
	);
};

ViewAppointmentBox.displayName = "ViewAppointmentBox";

type DetailsProps = {
	appointment: DetailsAppointmentInformation;
	coordinate: Coordinate;
	timezone: TimeZone;
	onClose: () => void;
	onIsBusyChange: (isBusy: boolean) => void;
	onAfterCancelOrDelete: (calendarIds: readonly string[]) => void;
	openConfirmationDialog: (props: ConfirmationDialogInitialData) => Promise<TryResult<void>>;
	onEdit: (
		coordinate: Coordinate,
		data: EditAppointmentInfo
	) => void;
	onCancelDialogStateChange: (state: CancelDialogState) => void;
	onActionsChange: (actions: readonly DetailsBoxAction[]) => void;
};

const Details = (props: DetailsProps): ReactElement => {
	const {
		appointment,
		coordinate,
		timezone,
		onIsBusyChange,
		onClose,
		onAfterCancelOrDelete,
		openConfirmationDialog,
		onEdit,
		onCancelDialogStateChange,
		onActionsChange
	} = props;

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const { emailAddress: currentUserEmailAddress } = userConfiguration();

	const isCurrentUserTheOrganizer: boolean = appointment.organizer && currentUserEmailAddress
		? appointment.organizer
			.emailAddress
			.toUpperCase() === currentUserEmailAddress.toUpperCase()
		: false;

	const myResponseStatus: AppointmentResponseStatus | null = appointment.myResponseStatus;
	const isResponseOutstanding: boolean = myResponseStatus?.isResponseOutstanding ?? false;

	const canRespond: boolean = !appointment.isCancelled
		&& !appointment.isOrganizer
		&& appointment.calendar
			.canEdit;

	const attendanceStatusSummary = useMemo(
		(): string | undefined => {
			const attendees: readonly AttendeeModel[] = appointment.attendees
				.filter(x => x.kind !== AttendeeKind.Resource);

			if (attendees.length === 0) {
				return undefined;
			}

			const groupedByAttendees: Partial<Record<AttendingStatus, AttendeeModel[]>> = groupBy(
				attendees,
				x => x.status
			);

			const attendanceStatusCountsAndLocalizations: AttendanceStatusCountAndLocalization[] = [
				{
					count: (groupedByAttendees[AttendingStatus.Accept] ?? []).length,
					localizationKey: "_AcceptedAmount"
				},
				{
					count: (groupedByAttendees[AttendingStatus.Decline] ?? []).length,
					localizationKey: "_DeclinedAmount"
				},
				{
					count: (groupedByAttendees[AttendingStatus.Tenative] ?? []).length,
					localizationKey: "_TentativeAmount"
				},
				{
					count: (groupedByAttendees[AttendingStatus.Unknown] ?? []).length,
					localizationKey: "_UnknownAmount"
				}
			];

			return attendanceStatusCountsAndLocalizations.filter(x => x.count > 0)
				.map(
					x=> localize(x.localizationKey)
						.replace(
							"{{Amount}}",
							x.count.toString()
						)
				)
				.join(", ");
		},
		[appointment]
	);

	const respondHandler = useCallback(
		async (
			appointment: DetailsAppointmentInformation,
			responseKind: ResponseKind
		): Promise<void> => {
			const respondId: RespondId = appointment.masterId
				? { master: appointment.masterId }
				: { appointment: appointment.id };

			try {
				onIsBusyChange(true);

				await sendAppointmentResponse(
					respondId,
					appointment
						.calendar
						.id,
					responseKind,
					alertManager
				);

				onClose();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				onIsBusyChange(false);
			}
		},
		[
			alertManager,
			onClose,
			onIsBusyChange
		]
	);

	const shouldShowAttendees: boolean = isNotNil(attendanceStatusSummary) || !isCurrentUserTheOrganizer;

	const descriptionText = useMemo(
		(): string => {
			if (isNil(appointment.description)) {
				return "";
			}

			switch (appointment.descriptionKind) {
				case ExchangeMessageBodyKind.Text:
					return appointment.description;
				case ExchangeMessageBodyKind.Html:
					return htmlToPlainText(appointment.description);
				default:
					never(appointment.descriptionKind);
			}
		},
		[appointment]
	);

	const deleteHandler = useCallback(
		async (recurringOperationKind: RecurringAppointmentOperationKind | undefined): Promise<void> => {
			onIsBusyChange(true);

			const result: TryResult<void> = await openConfirmationDialog({
				title: getAppointmentDeleteMessage(recurringOperationKind),
				confirmText: localize("_Delete"),
				trackingId: "delete-appointment-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				onIsBusyChange(false);
				return;
			}

			try {
				await deleteAppointment(
					{
						identifier: {
							id: appointment.id,
							calendarId: appointment.calendar.id
						},
						recurringOperationKind: isNil(recurringOperationKind)
							? { isSet: false }
							: { isSet: true, value: recurringOperationKind }
					},
					globalCancellation
				);

				onClose();
				onAfterCancelOrDelete([
					appointment.calendar
						.id
				]);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));

				onIsBusyChange(false);
			}
		},
		[
			onAfterCancelOrDelete,
			alertManager,
			appointment,
			globalCancellation,
			onClose,
			openConfirmationDialog,
			onIsBusyChange
		]
	);

	const editHandler = useCallback(
		(recurringOperationKind?: RecurringAppointmentOperationKind): void => {
			onEdit(
				coordinate,
				{
					appointment,
					recurringOperationKind: recurringOperationKind
				}
			);

			onClose();
		},
		[
			appointment,
			coordinate,
			onClose,
			onEdit,
		]
	);

	const openCancelDialogHandler = useCallback(
		(recurringAppointmentOperationKind: RecurringAppointmentOperationKind | undefined): void => {
			onCancelDialogStateChange({
				isOpen: true,
				recurringAppointmentOperationKind
			});
		},
		[onCancelDialogStateChange]
	);

	const actions = useMemo(
		(): readonly DetailsBoxAction[] => getViewAppointmentBoxActions(
			appointment,
			editHandler,
			deleteHandler,
			openCancelDialogHandler
		),
		[
			appointment,
			deleteHandler,
			editHandler,
			openCancelDialogHandler
		]
	);

	useEffect(
		(): void => {
			onActionsChange(actions);
		},
		[
			actions,
			onActionsChange
		]
	);

	const formattedAppointmentLocation = useMemo(
		(): string | null => {
			return formatCalendarLocations(
				appointment.locations
					.map(x => x.displayName) ?? []
			);
		},
		[appointment]
	);

	return (
		<DetailsBox.List>
			<DetailsBox.Item icon="clock">
				<AppointmentSpan
					appointment={appointment}
					timezone={timezone}
				/>
			</DetailsBox.Item>
			{(isNotNil(formattedAppointmentLocation) || appointment.meeting) && (
				<DetailsBox.Item icon="location-dot">
					{formattedAppointmentLocation}
					{appointment.meeting && (
						<div className={styles.buttonContainer}>
							<OnlineMeetingButton
								product={appointment.meeting.product}
								url={appointment.meeting.url}
							/>
						</div>
					)}
				</DetailsBox.Item>
			)}
			{appointment.calendar.name && (
				<DetailsBox.Item icon="calendar">
					{appointment.calendar.name}
				</DetailsBox.Item>
			)}
			{isNotNil(appointment.id) && isNotNil(appointment.reminderBeforeInMinutes) && (
				<DetailsBox.Item icon="bell">
					<ReminderText durationInMinutes={appointment.reminderBeforeInMinutes} />
				</DetailsBox.Item>
			)}
			{shouldShowAttendees && (
				<>
					<DetailsBox.Divider />
					<DetailsBox.SubHeader>{localize("_Attendees")}</DetailsBox.SubHeader>
					{appointment.organizer && (
						<div className={styles.organizer}>
							<OrganizerDetail
								displayName={appointment.organizer.displayName}
								emailAddress={appointment.organizer.emailAddress}
							/>
						</div>
					)}
					{attendanceStatusSummary && (
						<DetailsBox.Item icon="user-group">
							{attendanceStatusSummary}
						</DetailsBox.Item>
					)}
				</>
			)}
			{isNotNilOrWhiteSpace(descriptionText) && (
				<>
					<DetailsBox.Divider />
					<DetailsBox.SubHeader>{localize("_Description")}</DetailsBox.SubHeader>
					<DetailsBox.Item icon="align-left">
						<div className={styles.description}>
							{descriptionText}
						</div>
					</DetailsBox.Item>
				</>
			)}

			{canRespond && isResponseOutstanding && (
				<>
					<DetailsBox.Divider />
					<DetailsBox.Item icon="calendar-update">
						{appointment.organizer && (
							<FormattedText
								text={localize("_NameInvitedYouToThisEvent")}
								thingsToReplace={[
									{
										what: "{{Name}}",
										filler: appointment.organizer
											.displayName
									}
								]}
							/>
						)}
						<div className={styles.buttonContainer}>
							<Toolbar>
								<Toolbar.Button
									kind="secondary"
									text={localize("_Accept")}
									onClick={() => respondHandler(appointment, "accept")}
									icon="check"
									trackingId="accept"
								/>
								<Toolbar.Button
									kind="secondary"
									text={localize("_Decline")}
									onClick={() => respondHandler(appointment, "decline")}
									icon="xmark"
									trackingId="decline"
								/>
							</Toolbar>
						</div>
					</DetailsBox.Item>
				</>
			)}
			{actions.length > 0 && (
				<DetailsBox.Divider />
			)}
		</DetailsBox.List>
	);
};
Details.displayName = "Details";

type AttendanceStatusCountAndLocalization = {
	count: number;
	localizationKey: LocalizationKey;
};

type CancelDialogState = {
	isOpen: false;
} | {
	isOpen: true;
	recurringAppointmentOperationKind?: RecurringAppointmentOperationKind;
};

type ActionToGenerateOptions = {
	trackingId: string;
	text: string;
	onClick: (recurringOperationKind?: RecurringAppointmentOperationKind) => void;
	icon: FontIconName;
};

function getViewAppointmentBoxActions(
	appointment: AppointmentDetailModel | null,
	editHandler: (recurringOperationKind?: RecurringAppointmentOperationKind) => void,
	deleteHandler: (recurringOperationKind?: RecurringAppointmentOperationKind) => Promise<void>,
	cancelHandler: (recurringAppointmentOperationKind?: RecurringAppointmentOperationKind) => void
): readonly DetailsBoxAction[] {
	if (isNil(appointment)) {
		return EMPTY_ARRAY;
	}

	const shouldShowDeleteButton: boolean = computeShouldShowDeleteButton(appointment);
	const shouldShowCancelButton: boolean = computeShouldShowCancelButton(appointment);
	const shouldShowEditButton: boolean = computeShouldShowEditButton(appointment);

	const isSeries: boolean = isNotNil(appointment.masterId);

	const actions: DetailsBoxAction[] = [];

	if (shouldShowEditButton) {
		actions.push(
			...generateAppointmentAction(
				{
					trackingId: "edit",
					text: localize("_Edit"),
					onClick: editHandler,
					icon: "pen"
				},
				isSeries
			)
		);
	}

	if (shouldShowDeleteButton) {
		actions.push(
			...generateAppointmentAction(
				{
					trackingId: "delete",
					text: localize("_Delete"),
					onClick: deleteHandler,
					icon: "trash-can"
				},
				isSeries
			)
		);
	}

	if (shouldShowCancelButton) {
		actions.push(
			...generateAppointmentAction(
				{
					trackingId: "cancel",
					text: localize("_Cancel"),
					onClick: cancelHandler,
					icon: "xmark"
				},
				isSeries
			)
		);
	}

	return actions;
}

function generateAppointmentAction(
	actionToGenerateOptions: ActionToGenerateOptions,
	isSeries: boolean
): DetailsBoxAction[] {
	const {
		icon,
		onClick,
		text,
		trackingId
	} = actionToGenerateOptions;

	if (isSeries) {
		const group: DetailsBoxActionGroup = {
			name: trackingId,
			kind: "secondary",
			text,
			icon
		};

		return [
			{
				kind: "secondary",
				onClick: () => onClick(RecurringAppointmentOperationKind.Current),
				text: localize("_ThisEventOnly"),
				trackingId: generateTrackingId(trackingId, "|event-only"),
				group
			},
			{
				kind: "secondary",
				onClick: () => onClick(RecurringAppointmentOperationKind.CurrentAndFollowing),
				text: localize("_ThisAndAllFollowingEvents"),
				trackingId: generateTrackingId(trackingId, "|event-and-following"),
				group
			},
			{
				kind: "secondary",
				onClick: () => onClick(RecurringAppointmentOperationKind.All),
				text: localize("_AllEventsInTheSeries"),
				trackingId: generateTrackingId(trackingId, "|all-events"),
				group
			}
		];
	}

	return [{
		kind: "secondary",
		onClick: () => onClick(),
		text,
		trackingId,
		icon
	}];
}

const operationKindToDeleteMessageMap: {
	[key in RecurringAppointmentOperationKind]: LocalizationKey;
} = {
	[RecurringAppointmentOperationKind.All]: "_AreYouSureYouWantToDeleteAllEventsInTheSeries",
	[RecurringAppointmentOperationKind.CurrentAndFollowing]: "_AreYouSureYouWantToDeleteThisAndAllFollowingEventsInTheSeries",
	[RecurringAppointmentOperationKind.Current]: "_AreYouSureYouWantToDeleteThisEvent"
};

export function getAppointmentDeleteMessage(
	recurringOperationKind: RecurringAppointmentOperationKind | undefined
): string {
	return localize(
		isNotNil(recurringOperationKind)
			? operationKindToDeleteMessageMap[recurringOperationKind]
			: "_AreYouSureYouWantToDeleteThisEvent"
	);
}

export function computeShouldShowCancelButton(appointment: AppointmentDetailModel): boolean {
	return appointment.isOrganizer
		&& appointment.attendees
			.length > 0;
}

export function computeShouldShowDeleteButton(appointment: AppointmentDetailModel): boolean {
	const {
		myResponseStatus,
		calendar,
		isCancelled,
		isOrganizer,
		attendees
	} = appointment;

	const responseKind: AppointmentResponseKind | undefined = myResponseStatus
		?.responseKind;

	const isAttending: boolean = responseKind === AppointmentResponseKind.Accept
		|| responseKind === AppointmentResponseKind.Organizer
		|| responseKind === AppointmentResponseKind.Tentative;

	return calendar.canEdit
		&& (
			(isCancelled && isAttending) || (isOrganizer && attendees.length === 0)
		);
}

export function computeShouldShowEditButton(appointment: AppointmentDetailModel): boolean {
	return !appointment.isCancelled
		&& appointment.calendar
			.canEdit
		&& appointment
			.isOrganizer;
}